import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import SitemapUrlModal from './SitemapUrlModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Компонент для сортируемого ключевого слова
const SortableKeywordItem = ({ keyword, index, onKeywordChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, currentLang }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `keyword-${index}`,
    data: {
      type: 'keyword',
      index: index,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`transition-all duration-200 ${
        isDragging ? 'shadow-xl scale-105 rotate-2 z-10' : ''
      }`}
    >
      <div className="flex items-center space-x-2">
        {/* Кнопки сортировки для мобильных */}
        <div className="flex flex-col gap-0.5 sm:hidden">
          <button
            onClick={() => onMoveUp(index)}
            disabled={isFirst}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вверх' : 'Move up'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={isLast}
            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={currentLang === 'ru' ? 'Переместить вниз' : 'Move down'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Иконка для перетаскивания */}
        <div
          {...listeners}
          className="hidden sm:flex flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-full items-center justify-center cursor-move transition-all duration-200 hover:scale-110 active:scale-95 touch-manipulation"
          title={currentLang === 'ru' ? 'Перетащить для сортировки' : 'Drag to sort'}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        <input
          type="text"
          value={keyword}
          onChange={(e) => onKeywordChange(index, e.target.value)}
          className="flex-1 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
          placeholder={currentLang === 'ru' ? 'Введите ключевое слово' : 'Enter keyword'}
        />
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 sm:p-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex-shrink-0"
          title={currentLang === 'ru' ? 'Удалить' : 'Remove'}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const CreatePageModal = ({ siteId, site, isOpen, onClose, onPageCreated }) => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken } = useAuth();
  const [formData, setFormData] = useState({
    url: '',
    weight: 0,
    text: '',
    keywords: ['']
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sitemapModal, setSitemapModal] = useState({ isOpen: false });
  const urlTextareaRef = useRef(null);
  const [existingPages, setExistingPages] = useState([]);
  const [loadingExistingPages, setLoadingExistingPages] = useState(false);

  // Загрузка существующих страниц при открытии модалки
  useEffect(() => {
    if (isOpen && siteId) {
      fetchExistingPages();
    }
  }, [isOpen, siteId]);

  // Функция для загрузки существующих страниц
  const fetchExistingPages = async () => {
    try {
      setLoadingExistingPages(true);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.pages) {
          // Извлекаем URL из существующих страниц
          const existingUrls = data.pages.map(page => page.url).filter(Boolean);
          setExistingPages(existingUrls);
        }
      }
    } catch (error) {
      // Не показываем ошибку пользователю, так как это не критично
    } finally {
      setLoadingExistingPages(false);
    }
  };

  // Настройка сенсоров для Drag and Drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeywordChange = (index, value) => {
    const newKeywords = [...formData.keywords];
    newKeywords[index] = value;
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const addKeyword = () => {
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, '']
    }));
  };

  const removeKeyword = (index) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  };

  // Функции для перемещения ключевых слов
  const moveKeywordUp = (index) => {
    if (index <= 0) return;
    
    const newKeywords = [...formData.keywords];
    const temp = newKeywords[index];
    newKeywords[index] = newKeywords[index - 1];
    newKeywords[index - 1] = temp;
    
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const moveKeywordDown = (index) => {
    if (index >= formData.keywords.length - 1) return;
    
    const newKeywords = [...formData.keywords];
    const temp = newKeywords[index];
    newKeywords[index] = newKeywords[index + 1];
    newKeywords[index + 1] = temp;
    
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const isFirstKeyword = (index) => {
    return index === 0;
  };

  const isLastKeyword = (index) => {
    return index === formData.keywords.length - 1;
  };

  const encodeKeywordsToPHP = (keywords) => {
    if (!keywords || keywords.length === 0) return '';
    
    const encodedKeywords = keywords.map(keyword => {
      const encoded = keyword.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `s:${encoded.length}:"${encoded}"`;
    });
    
    return `a:${keywords.length}:{${encodedKeywords.map((kw, index) => `i:${index};${kw}`).join(';')}}`;
  };

  // Функция для автоматического изменения размера textarea
  const adjustTextareaHeight = () => {
    const textarea = urlTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Эффект для изменения размера при изменении содержимого
  useEffect(() => {
    adjustTextareaHeight();
  }, [formData.url]);

  const handleCreatePage = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      // Парсим URLs из textarea
      const urls = formData.url
        .split('\n')
        .map(url => url.trim())
        .filter(url => url.length > 0);

      if (urls.length === 0) {
        setError('Пожалуйста, введите хотя бы один URL');
        return;
      }

      // Формируем данные для отправки
      const requestData = {
        weight: parseInt(formData.weight),
        text: formData.text,
        key: encodeKeywordsToPHP(formData.keywords.filter(kw => kw.trim()))
      };

      // Если одна страница - отправляем как объект, если несколько - как массив
      if (urls.length === 1) {
        requestData.url = urls[0];
      } else {
        requestData.url = urls;
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Сбрасываем форму
        setFormData({
          url: '',
          weight: 0,
          text: '',
          keywords: ['']
        });
        // Закрываем модалку
        onClose();
        // Уведомляем родительский компонент
        if (onPageCreated) {
          onPageCreated();
        }
      } else {
        setError(data.message || 'Ошибка создания страницы');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message || 'Ошибка сети при создании');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      url: '',
      weight: 0,
      text: '',
      keywords: ['']
    });
    setError(null);
    onClose();
  };

  const handleUrlSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      url: prev.url ? `${prev.url}\n${url}` : url
    }));
  };

  // Функция для получения уже добавленных URL из textarea
  const getAddedUrls = () => {
    return formData.url
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);
  };

  // Функция для получения всех URL, которые уже существуют (в БД + в форме)
  const getAllExistingUrls = () => {
    const formUrls = getAddedUrls();
    return [...existingPages, ...formUrls];
  };

  // Функция для обработки мульти-выбора URL
  const handleMultiUrlSelect = (urls) => {
    const allExistingUrls = getAllExistingUrls();
    const newUrls = urls.filter(url => !allExistingUrls.includes(url));
    
    if (newUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        url: prev.url ? `${prev.url}\n${newUrls.join('\n')}` : newUrls.join('\n')
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                {currentLang === 'ru' ? 'Создать новую страницу' : 'Create New Page'}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4 sm:space-y-5">
              {/* URL */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {currentLang === 'ru' ? 'URL страницы(ниц):' : 'Page URL(s):'} *
                  </label>
                  {site?.sitemaps && (
                    <button
                      type="button"
                      onClick={() => setSitemapModal({ isOpen: true })}
                      className="px-2.5 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                      title={currentLang === 'ru' ? 'Выбрать из sitemap' : 'Select from sitemap'}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>{currentLang === 'ru' ? 'Sitemap' : 'Sitemap'}</span>
                    </button>
                  )}
                </div>
                <textarea
                  ref={urlTextareaRef}
                  value={formData.url}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors resize-none min-h-[60px]"
                  placeholder={currentLang === 'ru' ? 'https://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3' : 'https://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3'}
                  rows="1"
                  required
                />
                <div className="mt-1 text-xs text-gray-500">
                  {currentLang === 'ru' ? 'Введите один URL на строку для добавления нескольких страниц' : 'Enter one URL per line to add multiple pages'}
                </div>
              </div>

              {/* Вес страницы */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Вес для сортировки страниц:' : 'Page Weight for sorting:'}
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                  min="0"
                  max="100"
                />
              </div>

              {/* Внутренний комментарий */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Внутренний комментарий:' : 'Internal Comment:'}
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  rows="3"
                  className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors resize-none"
                  placeholder={currentLang === 'ru' ? 'Введите комментарий к странице' : 'Enter page comment'}
                />
              </div>

            </div>

            {/* Кнопки действий */}
            <div className="flex justify-end space-x-2 sm:space-x-3 mt-5 sm:mt-6">
              <button
                onClick={handleClose}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                disabled={loading}
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                onClick={handleCreatePage}
                disabled={loading || !formData.url.trim()}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-1.5 sm:space-x-2">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                    <span>{currentLang === 'ru' ? 'Создание...' : 'Creating...'}</span>
                  </div>
                ) : (
                  currentLang === 'ru' ? 'Создать' : 'Create'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Модалка выбора URL из sitemap */}
      <SitemapUrlModal
        site={site}
        isOpen={sitemapModal.isOpen}
        onClose={() => setSitemapModal({ isOpen: false })}
        onUrlSelect={handleUrlSelect}
        onMultiUrlSelect={handleMultiUrlSelect}
        addedUrls={getAddedUrls()}
        existingUrls={existingPages}
        multiSelect={true}
        loadingExistingPages={loadingExistingPages}
      />
    </>
  );
};

export default CreatePageModal; 